import React, { useState, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { ToastContainer, toast } from "react-toastify";
import Cookies from 'js-cookie';
import axios from "axios";
import { url } from '../../../../../../../../../App';
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import "./Expenses.css";

const CattleFeed = ({ loanId, visible, addExpense, editExpense, deleteExpense, isDisbursed }) => {
    const [open, setOpen] = React.useState(false);
    const token = Cookies.get('Token');
    const email = Cookies.get('Email');
    const [cattleExpenses, setCattleExpenses] = useState([
        { cattle_feeds: '', bag_units: "", purchase: '', quantity: "" },
    ]);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [editingRowIndex, setEditingRowIndex] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    useEffect(() => {
        if (!loanId) return;
        const fetchExpenses = async () => {
            setOpen(true);
            try {
                const response = await axios.post(url + '/cam/get/cattle/expense', {
                    token,
                    email,
                    loanId
                });

                if (response.data.auth) {
                    const fetchedExpenses = response.data.expenses;

                    if (fetchedExpenses && fetchedExpenses.length > 0) {
                        setCattleExpenses(fetchedExpenses);
                        setIsSubmitted(true);
                        setEditingRowIndex(null);
                    } else {
                        // If no data in DB, then show one empty row
                        setCattleExpenses([
                            { cattle_feeds: '', bag_units: "", purchase: '', quantity: "" }
                        ]);
                        setIsSubmitted(false);
                    }
                }
                else {
                    setCattleExpenses([
                        { cattle_feeds: '', bag_units: "", purchase: '', quantity: "" }
                    ]);
                    setIsSubmitted(false);
                }
            } catch (err) {
                console.error('Error fetching cattleExpenses:', err);
            } finally {
                setOpen(false);
            }
        };

        fetchExpenses();
    }, [loanId, email, token]);

    const handleChange = (index, field, value) => {
        const newExpenses = [...cattleExpenses];
        newExpenses[index][field] = value;
        setCattleExpenses(newExpenses);
    };

    const addRow = () => {
        const newIndex = cattleExpenses.length;
        setCattleExpenses([
            ...cattleExpenses,
            { cattle_feeds: '', bag_units: "", purchase: '', quantity: "" },
        ]);
        setEditingRowIndex(newIndex);
        setIsEditMode(false);
    };

    const handleDelete = async (expenseId, index) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this feed?");
        if (!confirmDelete) return;

        // Case 1: No expenseId → Just remove empty rows from frontend
        if (!expenseId) {
            setCattleExpenses(cattleExpenses?.filter((_, i) => i !== index));
            toast.success("Empty row removed.");
            return;
        }
        // Case 2: expenseId exists → Delete from DB
        try {
            setOpen(true);
            const response = await axios.post(url + '/cam/delete/cattle/expense', {
                token,
                email,
                loanId,
                expenseId
            });

            if (response.data.auth) {
                setCattleExpenses(response.data.data);
                toast.success(response.data.message);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error('Delete error:', error);
        } finally {
            setOpen(false);
        }
    };

    const handleSubmit = async () => {
        if (!cattleExpenses || cattleExpenses.length === 0) {
            toast.error("Please add at least one Cattle Feed before submitting.");
            return;
        }
        const isAnyFieldEmpty = cattleExpenses?.some(exp =>
            !exp.cattle_feeds.trim() ||
            !exp.bag_units.trim() ||
            !exp.purchase.trim() ||
            !exp.quantity.trim()
        );

        if (isAnyFieldEmpty) {
            toast.error("All cattle fields are mandatory.");
            return;
        }
        setOpen(true);
        try {
            const response = await axios.post(url + '/cam/add/cattle/expense', {
                token,
                email,
                loanId: loanId,
                cattle_expenses: cattleExpenses,
            });
            if (response.data.auth) {
                setCattleExpenses(response.data.data);
                setIsSubmitted(true);
                setEditingRowIndex(null);
                setIsEditMode(false);
                toast.success(response.data.message)
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error("Error fetching CAM data:", error);
        } finally {
            setOpen(false);
        }
    };

    const totalFeedCost = cattleExpenses?.reduce((sum, item) => {
        const a = Number(item.bag_units) || 0;
        const b = Number(item.purchase) || 0;
        const c = Number(item.quantity) || 0;
        return sum + (a * b * c);
    }, 0);

    return (
        <div className='main-container'>
            {loanId && (
                <>
                    <h3 style={{ textAlign: "center" }}>Cost of cattle feeds - Monthly</h3>
                    <div className="expense-container">
                        <table border="1" cellPadding="8" style={{ borderCollapse: 'collapse', width: '100%' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#cfe2f3' }}>
                                    <th>Sr. no</th>
                                    <th>Cattle feeds</th>
                                    <th>(A) Bag/Bori/Katta</th>
                                    <th>(B) Purchase (Time)</th>
                                    <th>(C) Per 1 Qty Price</th>
                                    <th>Amount (A x B x C)</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cattleExpenses?.map((exp, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>
                                            <input
                                                type="text"
                                                value={exp.cattle_feeds}
                                                onChange={(e) => handleChange(index, 'cattle_feeds', e.target.value)}
                                                disabled={(isSubmitted && editingRowIndex !== index) || isDisbursed}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                min="0"
                                                value={exp.bag_units}
                                                onChange={(e) => handleChange(index, 'bag_units', e.target.value)}
                                                disabled={(isSubmitted && editingRowIndex !== index) || isDisbursed}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                min="0"
                                                value={exp.purchase}
                                                onChange={(e) => handleChange(index, 'purchase', e.target.value)}
                                                disabled={(isSubmitted && editingRowIndex !== index) || isDisbursed}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                min="0"
                                                value={exp.quantity}
                                                onChange={(e) => handleChange(index, 'quantity', e.target.value)}
                                                disabled={(isSubmitted && editingRowIndex !== index) || isDisbursed}
                                            />
                                        </td>
                                        <td>{Number(exp.bag_units) * Number(exp.purchase) * Number(exp.quantity) || 0}</td>
                                        <td>
                                            {(deleteExpense || visible) && !isDisbursed && (
                                                <Tooltip title="Delete Cattle Feed">
                                                    <IconButton onClick={() => handleDelete(exp._id, index)}>
                                                        <DeleteIcon sx={{ color: 'red' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {(editExpense || visible) && !isDisbursed && (
                                                <Tooltip title="Edit Feed">
                                                    <IconButton
                                                        onClick={() => {
                                                            setEditingRowIndex(index);
                                                            setIsEditMode(true);
                                                        }}
                                                    >
                                                        <EditIcon sx={{ color: '#034c9b' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </td>
                                    </tr>
                                ))}

                                <tr style={{ backgroundColor: '#e2f0d9' }}>
                                    <td colSpan="5"><strong>Total Amount of feeds</strong></td>
                                    <td colSpan="2"><strong>{totalFeedCost}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    {(addExpense || visible) && !isDisbursed && (
                        <button type="submit" className="expense-btn" onClick={addRow}>
                            Add Row
                        </button>
                    )}
                    {(addExpense || visible) && !isDisbursed && (
                        <button type="submit" className="expense-btn" onClick={handleSubmit}>
                            {isEditMode ? 'Update' : 'Submit'}
                        </button>
                    )}
                </>
            )}
            <ToastContainer />
            <Backdrop
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={open}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </div>
    );
}

export default CattleFeed;